'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var DAYS_IN_WEEK = 7;
var SUNDAY = 0;
var MONDAY = 1;

function getGridContext(gridDesign, gridDate, width, height, d2f, firstDayOfWeek, barWidth, hideWeekNum) {
    var currentDate = new Date(gridDate.year, gridDate.month, 1);
    // subtract firstDayOfWeek to make the week start by it
    var firstDayOfTheMonth = (currentDate.getDay() + DAYS_IN_WEEK - (firstDayOfWeek % DAYS_IN_WEEK)) % DAYS_IN_WEEK;
    var lastDayOfTheMonth = new Date(gridDate.year, gridDate.month + 1, 0);
    var dayInTheMonthCount = lastDayOfTheMonth.getDate();
    lastDayOfTheMonth.setMonth(gridDate.month, 0);
    var daysInPrevMonthCount = lastDayOfTheMonth.getDate();
    // we always display at least 5 rows
    var weekCount = Math.max(5, Math.ceil((dayInTheMonthCount + firstDayOfTheMonth) / DAYS_IN_WEEK));
    var padding = gridDesign.padding, cellSpacing = gridDesign.cellSpacing;
    var cellWidth = (width - d2f(padding.left + padding.right + (DAYS_IN_WEEK - 1) * cellSpacing.horizontal)) / DAYS_IN_WEEK;
    var cellHeight = (height - d2f(padding.top + padding.bottom + (weekCount - 1) * cellSpacing.vertical)) / weekCount;
    if ((firstDayOfWeek !== MONDAY && firstDayOfWeek !== SUNDAY) || hideWeekNum) {
        cellWidth += barWidth / DAYS_IN_WEEK;
    }
    return {
        cellWidth: cellWidth,
        cellHeight: cellHeight,
        weekCount: weekCount,
        firstDayOfTheMonth: firstDayOfTheMonth,
        dayInTheMonthCount: dayInTheMonthCount,
        daysInPrevMonthCount: daysInPrevMonthCount,
    };
}

function getCellDesignValue(key, designs) {
    var value = undefined;
    designs.forEach(function (design) {
        if (design[key] !== undefined) {
            value = design[key];
        }
    });
    return value;
}
function getApplicableCellDesign(cellDesigns, context) {
    var _a;
    var designs = [cellDesigns.base];
    (_a = cellDesigns.overrides) === null || _a === void 0 ? void 0 : _a.forEach(function (override) {
        switch (override.type) {
            case 'notInMonth':
                if (!context.isInMonth) {
                    designs.push(override);
                }
                break;
            case 'left':
                if (context.positionH === 'left') {
                    designs.push(override);
                }
                break;
            case 'right':
                if (context.positionH === 'right') {
                    designs.push(override);
                }
                break;
            case 'top':
                if (context.positionV === 'top') {
                    designs.push(override);
                }
                break;
            case 'bottom':
                if (context.positionV === 'bottom') {
                    designs.push(override);
                }
                break;
            default:
                if (override.type === context.dayNumber) {
                    designs.push(override);
                }
        }
    });
    return designs;
}
function hAlignToTextAnchor(align) {
    switch (align) {
        case 'left': return 'start';
        case 'right': return 'end';
        default: return 'middle';
    }
}
function xTextAlign(left, width, padding, d2f, align) {
    switch (align) {
        case 'left': return left + d2f((padding === null || padding === void 0 ? void 0 : padding.left) || 0);
        case 'right': return left + width - d2f(((padding === null || padding === void 0 ? void 0 : padding.right) || 0));
        default: return left + width / 2;
    }
}
function yTextAlign(top, height, padding, fontSize, d2f, align) {
    switch (align) {
        case 'top': return top + d2f(((padding === null || padding === void 0 ? void 0 : padding.top) || 0) + fontSize);
        case 'bottom': return top + height - d2f(((padding === null || padding === void 0 ? void 0 : padding.bottom) || 0));
        default: return top + (height + d2f(fontSize)) / 2;
    }
}

function buildTextStyle(textStyle, d2f, opts) {
    var style = ''
        + ("fill:" + textStyle.color + ";")
        + ("font-family:" + textStyle.fontFamily + ";")
        + ("font-size:" + d2f(textStyle.fontSize) + "pt;");
    if (textStyle.fontStyle) {
        style += "font-style:" + textStyle.fontStyle + ";";
    }
    if (textStyle.fontWeight) {
        style += "font-weight:" + textStyle.fontWeight + ";";
    }
    if (textStyle.letterSpacing) {
        style += "letter-spacing:" + textStyle.letterSpacing + ";";
    }
    if (opts === null || opts === void 0 ? void 0 : opts.hAlignment) {
        style += "text-anchor:" + hAlignToTextAnchor(opts.hAlignment) + ";";
    }
    return style;
}

function applyFontTransform(text, transform) {
    switch (transform) {
        case 'none':
            return text;
        case 'uppercase':
            return text.toUpperCase();
        case 'lowercase':
            return text.toLowerCase();
        case 'capitalize':
            return text.replace(/\b\w/g, function (l) { return l.toUpperCase(); });
    }
}

function createDayBar(gridDate, height, locale, weekDayBarDesign, gridDesign, offsetCoords, gridContext, d2f, firstDayOfWeek) {
    var _a;
    var svg = '';
    var _b = weekDayBarDesign.base, label = _b.label, padding = _b.padding, _c = _b.hAlignment, hAlignment = _c === void 0 ? 'right' : _c;
    var intlWeekday = new Intl.DateTimeFormat(locale, { weekday: weekDayBarDesign.base.display || 'short' });
    var textStyle = buildTextStyle(label, d2f, { hAlignment: hAlignment });
    svg += "<g id=\"week-day-bar\" style=\"" + textStyle + "\">";
    var y = yTextAlign(offsetCoords.top, height, padding, label.fontSize, d2f, 'bottom');
    for (var i = 0; i < DAYS_IN_WEEK; i += 1) {
        // if sunday apply overridden styles
        var designOverride = weekDayBarDesign[(DAYS_IN_WEEK - firstDayOfWeek - i) % DAYS_IN_WEEK] ? weekDayBarDesign[(DAYS_IN_WEEK - firstDayOfWeek - i) % DAYS_IN_WEEK] : undefined;
        if (designOverride) {
            padding = designOverride.padding || padding;
            hAlignment = designOverride.hAlignment || hAlignment;
            label = designOverride.label || label;
            y = yTextAlign(offsetCoords.top, height, padding, label.fontSize, d2f, 'bottom');
        }
        var date = new Date(gridDate.year, gridDate.month, (i + 1 + DAYS_IN_WEEK - gridContext.firstDayOfTheMonth));
        var left = offsetCoords.left + (i % DAYS_IN_WEEK) * (gridContext.cellWidth + d2f(gridDesign.cellSpacing.horizontal));
        var x = xTextAlign(left, gridContext.cellWidth, padding, d2f, hAlignment);
        var styleOverride = (designOverride === null || designOverride === void 0 ? void 0 : designOverride.label) || (designOverride === null || designOverride === void 0 ? void 0 : designOverride.hAlignment) ? buildTextStyle(label, d2f, { hAlignment: hAlignment }) : undefined;
        var styleAttribute = styleOverride ? " style=\"" + styleOverride + "\"" : '';
        if (designOverride === null || designOverride === void 0 ? void 0 : designOverride.display) {
            intlWeekday = new Intl.DateTimeFormat(locale, { weekday: designOverride.display });
        }
        var text = intlWeekday.format(date);
        var fontTransform = label.fontTransform || ((_a = designOverride === null || designOverride === void 0 ? void 0 : designOverride.label) === null || _a === void 0 ? void 0 : _a.fontTransform) || 'none';
        if (fontTransform !== 'none') {
            text = applyFontTransform(text, fontTransform);
        }
        svg += "<text x=\"" + x + "\" y=\"" + y + "\"" + styleAttribute + ">" + text + "</text>";
    }
    svg += '</g>';
    return svg;
}

// following https://en.wikipedia.org/wiki/ISO_8601#Week_dates
// and https://www.epochconverter.com/weeknumbers
var WEEK_IN_MS = 1000 * 60 * 60 * 24 * 7;
var THURSDAY = 4;
function getWeekNumber(selectedDate) {
    var firstThursday = new Date(selectedDate);
    var targetDay = (selectedDate.getDay() + DAYS_IN_WEEK - 1) % DAYS_IN_WEEK; // day of the week with monday first
    firstThursday.setDate(selectedDate.getDate() - targetDay + THURSDAY - 1);
    var startOfTheYear = new Date(firstThursday.getFullYear(), 0, 1);
    if (startOfTheYear.getDay() != THURSDAY) {
        startOfTheYear.setMonth(0, 1 + ((THURSDAY - startOfTheYear.getDay()) + DAYS_IN_WEEK) % DAYS_IN_WEEK);
    }
    return 1 + Math.ceil((firstThursday.getTime() - startOfTheYear.getTime()) / WEEK_IN_MS);
}

var DAY_IN_MS = 1000 * 60 * 60 * 24;
var FIRST_WEEK_OF_YEAR = 1;
var LAST_DAY_OF_WEEK = 6; // Saturday will be the last day as US calendar will always start from Sunday
var DECEMBER = 11;
var getUsWeekNumber = function (selectedDate) {
    var startOfTheYear = new Date(selectedDate.getFullYear(), 0, 1);
    var weekNum = Math.ceil((((selectedDate.getTime() - startOfTheYear.getTime()) / DAY_IN_MS) + startOfTheYear.getDay() + 1) / DAYS_IN_WEEK);
    // if december check for next year starting
    if (selectedDate.getMonth() === DECEMBER) {
        for (var i = 1; i <= LAST_DAY_OF_WEEK - selectedDate.getDay(); i += 1) {
            var date = new Date(selectedDate);
            date.setDate(date.getDate() + i);
            if (date.getFullYear() > startOfTheYear.getFullYear()) {
                return FIRST_WEEK_OF_YEAR;
            }
        }
    }
    return weekNum;
};

var _a$b;
var weekNum = (_a$b = {},
    _a$b[SUNDAY] = getUsWeekNumber,
    _a$b[MONDAY] = getWeekNumber,
    _a$b);
function createWeekBar(gridDate, width, weekNumberDesign, gridDesign, offset, gridContext, d2f, firstDayOfWeek) {
    var svg = '';
    var number = weekNumberDesign.number;
    var weekDate = new Date(gridDate.year, gridDate.month, 1);
    var hAlignment = number.hAlignment || 'right';
    var textStyle = buildTextStyle(number.style, d2f, { hAlignment: hAlignment, vAlignment: number.vAlignment });
    svg += "<g id=\"week-number-bar\" style=\"" + textStyle + "\">";
    var x = xTextAlign(offset.left, width, number.padding, d2f, hAlignment);
    for (var i = 0; i < gridContext.weekCount; i += 1) {
        var top_1 = offset.top + i * (gridContext.cellHeight + d2f(gridDesign.cellSpacing.vertical));
        var y = yTextAlign(top_1, gridContext.cellHeight, number.padding, number.style.fontSize, d2f, number.vAlignment);
        var weekNumber = weekNum[firstDayOfWeek](weekDate);
        weekDate.setDate(weekDate.getDate() + DAYS_IN_WEEK);
        svg += "<text x=\"" + x + "\" y=\"" + y + "\">" + weekNumber + "</text>";
    }
    svg += '</g>';
    return svg;
}

function getWeekdayBarHeight(weekdayBarDesign, d2f) {
    if (!weekdayBarDesign) {
        return 0;
    }
    return d2f(weekdayBarDesign.base.padding.top + weekdayBarDesign.base.padding.bottom + weekdayBarDesign.base.label.fontSize);
}

function getWeekNumberColumnWidth(weekDesign, d2f) {
    if (!weekDesign) {
        return 0;
    }
    var _a = weekDesign.number, padding = _a.padding, size99 = _a.size99;
    return d2f(((padding === null || padding === void 0 ? void 0 : padding.left) || 0) + ((padding === null || padding === void 0 ? void 0 : padding.right) || 0) + size99);
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function getCellContext(cellIndex, gridDate, gridDesign, gridContext, offsetCoords, d2f, firstDayOfWeek) {
    var column = cellIndex % DAYS_IN_WEEK;
    var left = offsetCoords.left + column * (gridContext.cellWidth + d2f(gridDesign.cellSpacing.horizontal));
    var row = Math.floor(cellIndex / DAYS_IN_WEEK);
    var top = offsetCoords.top + row * (gridContext.cellHeight + d2f(gridDesign.cellSpacing.vertical));
    var positionH = 'center';
    if (column === 0) {
        positionH = 'left';
    }
    else if (column === DAYS_IN_WEEK - 1) {
        positionH = 'right';
    }
    var positionV = 'center';
    if (row === 0) {
        positionV = 'top';
    }
    else if (row === gridContext.weekCount - 1) {
        positionV = 'bottom';
    }
    var isInMonth = cellIndex >= gridContext.firstDayOfTheMonth && cellIndex < gridContext.dayInTheMonthCount + gridContext.firstDayOfTheMonth;
    var day = new Date(gridDate.year, gridDate.month, 1);
    day.setDate(cellIndex - gridContext.firstDayOfTheMonth + 1);
    return {
        day: day.getDate(),
        display: {
            left: left,
            top: top,
            width: gridContext.cellWidth, height: gridContext.cellHeight,
        },
        context: { positionH: positionH, positionV: positionV, isInMonth: isInMonth, dayNumber: (DAYS_IN_WEEK - firstDayOfWeek - column) % DAYS_IN_WEEK }
    };
}

var GridCellBackgrounds = /** @class */ (function () {
    function GridCellBackgrounds(base, d2f) {
        this.svg = '';
        this.d2f = d2f;
        this.baseBackgroundColor = getCellDesignValue('backgroundColor', [base]);
        this.baseBorderRadius = getCellDesignValue('borderRadius', [base]);
    }
    GridCellBackgrounds.prototype.addCell = function (_a, designs) {
        var _b;
        var left = _a.left, top = _a.top, width = _a.width, height = _a.height;
        var backgroundColor = getCellDesignValue('backgroundColor', designs);
        var props = [];
        if (backgroundColor && backgroundColor !== this.baseBackgroundColor) {
            props.push("style=\"fill:" + backgroundColor + ";\"");
        }
        var borderRadius = (_b = getCellDesignValue('borderRadius', designs)) !== null && _b !== void 0 ? _b : this.baseBorderRadius;
        if (borderRadius) {
            var value = this.d2f(borderRadius);
            props.push("rx=\"" + value + "\"", "ry=\"" + value + "\"");
        }
        if (props.length) {
            this.svg += "<rect width=\"" + width + "\" height=\"" + height + "\" x=\"" + left + "\" y=\"" + top + "\" " + props.join(' ') + "/>";
        }
    };
    GridCellBackgrounds.prototype.finalize = function () {
        if (!this.svg) {
            return '';
        }
        var props = [];
        if (this.baseBackgroundColor) {
            props.push("style=\"fill:" + this.baseBackgroundColor + ";\"");
        }
        return "<g id=\"cells-background\" " + props.join(' ') + ">" + this.svg + "</g>";
    };
    return GridCellBackgrounds;
}());

var GridCellBorders = /** @class */ (function () {
    function GridCellBorders(base, borderName, d2f) {
        this.svg = '';
        this.name = borderName;
        this.d2f = d2f;
        this.baseBorder = base[borderName];
    }
    GridCellBorders.getBorderStyle = function (border, baseBorder, d2f) {
        var style = '';
        if (border.color !== (baseBorder === null || baseBorder === void 0 ? void 0 : baseBorder.color)) {
            style += "stroke:" + border.color + ";";
        }
        if (border.size !== (baseBorder === null || baseBorder === void 0 ? void 0 : baseBorder.size)) {
            style += "stroke-width:" + d2f(border.size) + ";";
        }
        return style;
    };
    GridCellBorders.prototype.addCell = function (display, designs) {
        var border = getCellDesignValue(this.name, designs);
        if (!border) {
            return;
        }
        // TODO add support for border radius
        var props;
        switch (this.name) {
            case 'borderLeft':
                props = ["x1=\"" + display.left + "\"", "y1=\"" + display.top + "\"", "x2=\"" + display.left + "\"", "y2=\"" + (display.top + display.height) + "\""];
                break;
            case 'borderRight':
                props = ["x1=\"" + (display.left + display.width) + "\"", "y1=\"" + display.top + "\"", "x2=\"" + (display.left + display.width) + "\"", "y2=\"" + (display.top + display.height) + "\""];
                break;
            case 'borderTop':
                props = ["x1=\"" + display.left + "\"", "y1=\"" + display.top + "\"", "x2=\"" + (display.left + display.width) + "\"", "y2=\"" + display.top + "\""];
                break;
            default:
                props = ["x1=\"" + display.left + "\"", "y1=\"" + (display.top + display.height) + "\"", "x2=\"" + (display.left + display.width) + "\"", "y2=\"" + (display.top + display.height) + "\""];
                break;
        }
        var style = GridCellBorders.getBorderStyle(border, this.baseBorder, this.d2f);
        if (style) {
            props.push("style=\"" + style + "\"");
        }
        this.svg += "<line " + props.join(' ') + " />";
    };
    GridCellBorders.prototype.finalize = function () {
        if (!this.svg) {
            return '';
        }
        var baseBorderStyle = this.baseBorder && GridCellBorders.getBorderStyle(this.baseBorder, undefined, this.d2f);
        var openG = baseBorderStyle ? "<g id=\"cells-" + this.name + "\" style=\"" + baseBorderStyle + "\">" : "<g id=\"cells-" + this.name + "\">";
        return "" + openG + this.svg + "</g>";
    };
    return GridCellBorders;
}());

var GridCellNumbers = /** @class */ (function () {
    function GridCellNumbers(base, d2f) {
        this.d2f = d2f;
        this.baseHAlignment = base.hAlignment || 'right';
        this.baseVAlignment = base.vAlignment || 'top';
        this.baseStyle = base.date;
        var textStyle = buildTextStyle(this.baseStyle, d2f, { vAlignment: this.baseVAlignment, hAlignment: this.baseHAlignment });
        this.svg = "<g id=\"cells-number\" style=\"" + textStyle + "\">";
    }
    GridCellNumbers.prototype.addCell = function (display, designs, day) {
        var left = display.left, top = display.top, width = display.width, height = display.height;
        var cellPadding = getCellDesignValue('padding', designs);
        var dateDesign = getCellDesignValue('date', designs);
        var hAlignment = getCellDesignValue('hAlignment', designs);
        var vAlignment = getCellDesignValue('vAlignment', designs) || this.baseVAlignment;
        var cellTextSize = (dateDesign === null || dateDesign === void 0 ? void 0 : dateDesign.fontSize) || this.baseStyle.fontSize;
        var textStyleOverride = '';
        if (hAlignment && hAlignment !== this.baseHAlignment) {
            textStyleOverride += "text-anchor:" + hAlignToTextAnchor(hAlignment) + ";";
        }
        if (dateDesign) {
            if (dateDesign.color && dateDesign.color !== this.baseStyle.color) {
                textStyleOverride += "fill:" + dateDesign.color + ";";
            }
            if (dateDesign.fontFamily && dateDesign.fontFamily !== this.baseStyle.fontFamily) {
                textStyleOverride += "font-family:" + dateDesign.fontFamily + ";";
            }
            if (dateDesign.fontWeight && dateDesign.fontWeight !== this.baseStyle.fontWeight) {
                textStyleOverride += "font-weight:" + dateDesign.fontWeight + ";";
            }
            if (dateDesign.fontStyle && dateDesign.fontStyle !== this.baseStyle.fontStyle) {
                textStyleOverride += "font-style:" + dateDesign.fontStyle + ";";
            }
            if (dateDesign.fontSize && dateDesign.fontSize !== this.baseStyle.fontSize) {
                textStyleOverride += "font-size:" + this.d2f(dateDesign.fontSize) + "px;";
            }
        }
        var x = xTextAlign(left, width, cellPadding, this.d2f, hAlignment || this.baseHAlignment);
        var y = yTextAlign(top, height, cellPadding, cellTextSize, this.d2f, vAlignment);
        this.svg += "<text x=\"" + x + "\" y=\"" + y + "\"" + (textStyleOverride ? " style=\"" + textStyleOverride + "\"" : '') + (">" + day + "</text>");
        return { textHeight: y - top };
    };
    GridCellNumbers.prototype.finalize = function () {
        this.svg += '</g>';
        return this.svg;
    };
    return GridCellNumbers;
}());

function getEventLabelsSize(design, gridContext, d2f, events) {
    var _a, _b, _c;
    var paddings = design.eventsPadding;
    var cellDate = (_a = design.eventsText) !== null && _a !== void 0 ? _a : design.date;
    var defaultFontSize = cellDate.fontSize;
    var canvas = document.createElement('canvas');
    var ctx = canvas.getContext('2d');
    if (!ctx) {
        return defaultFontSize;
    }
    var frameWidth = gridContext.cellWidth - d2f((_b = paddings === null || paddings === void 0 ? void 0 : paddings.left) !== null && _b !== void 0 ? _b : 0) - d2f((_c = paddings === null || paddings === void 0 ? void 0 : paddings.right) !== null && _c !== void 0 ? _c : 0);
    events.forEach(function (_a) {
        var label = _a.label;
        ctx.font = d2f(defaultFontSize) + "px " + cellDate.fontFamily;
        defaultFontSize = Math.min(frameWidth / ctx.measureText(label).width * defaultFontSize, defaultFontSize);
    });
    return defaultFontSize;
}

var GridCellEvents = /** @class */ (function () {
    function GridCellEvents(base, d2f) {
        var _a, _b, _c, _d;
        this.count = 0;
        this.d2f = d2f;
        this.baseHAlignment = (_b = (_a = base.eventHAlignment) !== null && _a !== void 0 ? _a : base.hAlignment) !== null && _b !== void 0 ? _b : 'right';
        this.baseVAlignment = (_c = base.eventVAlignment) !== null && _c !== void 0 ? _c : 'bottom';
        this.baseStyle = (_d = base.eventsText) !== null && _d !== void 0 ? _d : base.date;
        var textStyle = buildTextStyle(this.baseStyle, d2f, { vAlignment: this.baseVAlignment, hAlignment: this.baseHAlignment });
        this.svg = "<g id=\"cells-events\" style=\"" + textStyle + "\">";
    }
    GridCellEvents.prototype.addCell = function (display, designs, maxFontSize, events) {
        var _this = this;
        var _a, _b, _c;
        this.count += 1;
        var left = display.left, top = display.top, width = display.width, height = display.height;
        var hAlignment = getCellDesignValue('eventHAlignment', designs);
        var vAlignment = getCellDesignValue('eventVAlignment', designs) || this.baseVAlignment;
        var text = getCellDesignValue('eventsText', designs);
        var eventsPadding = getCellDesignValue('eventsPadding', designs);
        var textStyleOverride = '';
        if (hAlignment && hAlignment !== this.baseHAlignment) {
            textStyleOverride += "text-anchor:" + hAlignToTextAnchor(hAlignment) + ";";
        }
        if (text) {
            if (text.color && text.color !== this.baseStyle.color) {
                textStyleOverride += "fill:" + text.color + ";";
            }
            if (text.fontFamily && text.fontFamily !== this.baseStyle.fontFamily) {
                textStyleOverride += "font-family:" + text.fontFamily + ";";
            }
            if (text.fontWeight && text.fontWeight !== this.baseStyle.fontWeight) {
                textStyleOverride += "font-weight:" + text.fontWeight + ";";
            }
            if (text.fontStyle && text.fontStyle !== this.baseStyle.fontStyle) {
                textStyleOverride += "font-style:" + text.fontStyle + ";";
            }
            if (text.fontSize && text.fontSize !== this.baseStyle.fontSize) {
                textStyleOverride += "font-size:" + this.d2f(Math.min(text.fontSize, maxFontSize)) + "px;";
            }
        }
        var topPadding = this.d2f((_a = eventsPadding === null || eventsPadding === void 0 ? void 0 : eventsPadding.top) !== null && _a !== void 0 ? _a : 0);
        var bottomPadding = this.d2f((_b = eventsPadding === null || eventsPadding === void 0 ? void 0 : eventsPadding.bottom) !== null && _b !== void 0 ? _b : 0);
        var eventsTextSize = this.d2f(Math.min((_c = text === null || text === void 0 ? void 0 : text.fontSize) !== null && _c !== void 0 ? _c : this.baseStyle.fontSize, maxFontSize));
        var lineHeight = eventsTextSize * 1.2;
        var bottomOffset = lineHeight * 0.15; // i don't know why we need it :'(
        var eventHeight = events.length * lineHeight + bottomOffset;
        var availableYSpace = height - bottomPadding - topPadding;
        var eventsTextStyle = textStyleOverride;
        if (availableYSpace < eventHeight) {
            lineHeight = availableYSpace / events.length;
            eventHeight = events.length * lineHeight + bottomOffset;
            eventsTextStyle += "font-size:" + lineHeight / 1.2 + "px;";
        }
        else {
            eventsTextStyle += "font-size:" + eventsTextSize + "px;";
        }
        var eventY = 0;
        switch (vAlignment) {
            case 'top':
                eventY = top + topPadding;
                break;
            case 'middle':
                eventY = top + (height - eventHeight) / 2;
                break;
            default:
            case 'bottom':
                eventY = top + height - (eventHeight + bottomPadding);
                break;
        }
        var x = xTextAlign(left, width, eventsPadding, this.d2f, hAlignment || this.baseHAlignment);
        this.svg += "<text x=\"" + x + "\" y=\"" + eventY + "\" style=\"" + eventsTextStyle + "\">";
        events.forEach(function (eventName) {
            _this.svg += "<tspan x=\"" + x + "\" dy=\"" + lineHeight + "\">" + eventName + "</tspan>";
        });
        this.svg += '</text>';
    };
    GridCellEvents.prototype.finalize = function () {
        if (this.count === 0) {
            return '';
        }
        this.svg += '</g>';
        return this.svg;
    };
    return GridCellEvents;
}());

function encodeStringForSVG(input) {
    return input
        .replace(/&/g, '&amp;') // Replace & with &amp;
        .replace(/</g, '&lt;') // Replace < with &lt;
        .replace(/>/g, '&gt;') // Replace > with &gt;
        .replace(/"/g, '&quot;') // Replace " with &quot;
        .replace(/'/g, '&#39;'); // Replace ' with &#39;
}

var BORDER_NAMES = ['borderTop', 'borderLeft', 'borderRight', 'borderBottom'];
function createGrid(gridDate, gridDesign, gridContext, offset, d2f, firstDayOfWeek, events) {
    var svg = '';
    var base = gridDesign.cell.base;
    var backgrounds = new GridCellBackgrounds(base, d2f);
    var borders = BORDER_NAMES.map(function (borderName) { return new GridCellBorders(base, borderName, d2f); });
    var numbers = new GridCellNumbers(base, d2f);
    var eventCells = new GridCellEvents(base, d2f);
    var maxLabelSize = getEventLabelsSize(base, gridContext, d2f, events);
    var _loop_1 = function (i) {
        var _a = getCellContext(i, gridDate, gridDesign, gridContext, offset, d2f, firstDayOfWeek), display = _a.display, day = _a.day, context = _a.context;
        var designs = getApplicableCellDesign(gridDesign.cell, context);
        var textHeight = numbers.addCell(display, designs, day).textHeight;
        var gridEvents = events.filter(function (e) { return e.day === day; }).map(function (e) { return encodeStringForSVG(e.label); });
        if (context.isInMonth && gridEvents.length) {
            eventCells.addCell(__assign(__assign({}, display), { top: display.top + textHeight, height: display.height - textHeight }), designs, maxLabelSize, gridEvents);
        }
        borders.forEach(function (border) { return border.addCell(display, designs); });
        backgrounds.addCell(display, designs);
    };
    for (var i = 0; i < gridContext.weekCount * DAYS_IN_WEEK; i += 1) {
        _loop_1(i);
    }
    svg += backgrounds.finalize();
    borders.forEach(function (border) { svg += border.finalize(); });
    svg += numbers.finalize();
    svg += eventCells.finalize();
    return svg;
}

function getToSizeFn(gridDesign, actualWidth) {
    return function (x) { return (x * actualWidth) / gridDesign.width; };
}

function generateGridAsSVG(gridDesign, gridDate, locale, width, height, events, options) {
    var _a, _b, _c;
    var d2f = (options === null || options === void 0 ? void 0 : options.d2f) || getToSizeFn(gridDesign, width);
    var firstDayOfWeek = (_a = options === null || options === void 0 ? void 0 : options.firstDayOfWeek) !== null && _a !== void 0 ? _a : MONDAY;
    var hideWeekNum = options === null || options === void 0 ? void 0 : options.hideWeekNum;
    var padding = gridDesign.grid.padding;
    var gridStyle = '';
    if (gridDesign.grid.backgroundColor) {
        gridStyle += "fill:" + gridDesign.grid.backgroundColor + ";";
    }
    var svg = "<svg width=\"" + ((_b = options === null || options === void 0 ? void 0 : options.svgWidth) !== null && _b !== void 0 ? _b : width) + "\" height=\"" + ((_c = options === null || options === void 0 ? void 0 : options.svgHeight) !== null && _c !== void 0 ? _c : height) + "\" viewBox=\"0 0 " + width + " " + height + "\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">";
    if (gridStyle) {
        svg += "<rect width=\"" + width + "\" height=\"" + height + "\" style=\"" + gridStyle + "\" />";
    }
    var barHeight = getWeekdayBarHeight(gridDesign.dayLabel, d2f);
    var barWidth = getWeekNumberColumnWidth(gridDesign.weekLabel, d2f);
    var gridContext = getGridContext(gridDesign.grid, gridDate, width - barWidth, height - barHeight, d2f, firstDayOfWeek, barWidth, hideWeekNum);
    var offset = { top: d2f(padding.top), left: d2f(padding.left) };
    if (gridDesign.weekLabel && (firstDayOfWeek === MONDAY || firstDayOfWeek === SUNDAY) && !hideWeekNum) {
        svg += createWeekBar(gridDate, barWidth, gridDesign.weekLabel, gridDesign.grid, { left: offset.left, top: offset.top + barHeight }, gridContext, d2f, firstDayOfWeek);
        offset.left += barWidth;
    }
    if (gridDesign.dayLabel) {
        svg += createDayBar(gridDate, barHeight, locale, gridDesign.dayLabel, gridDesign.grid, { left: offset.left, top: offset.top }, gridContext, d2f, firstDayOfWeek);
    }
    offset.top += barHeight;
    svg += createGrid(gridDate, gridDesign.grid, gridContext, offset, d2f, firstDayOfWeek, events);
    svg += '</svg>';
    return svg;
}

var NAME_START = 'Month';
function isSpreadNameAMonth(spreadName) {
    return spreadName.startsWith(NAME_START);
}

var CURRENT_YEAR = new Date().getFullYear();
function getSpreadDate(spreadName, startDate) {
    var monthNumber = 1;
    if (spreadName && isSpreadNameAMonth(spreadName)) {
        var numberString = spreadName.slice(NAME_START.length);
        monthNumber = parseInt(numberString, 10);
    }
    if (!startDate) {
        return new Date(CURRENT_YEAR, monthNumber - 1, 1);
    }
    return new Date(startDate.year, monthNumber + startDate.month - 1, 1);
}

var PT_MM_RATIO = 0.352778;
var mm2pt = function (mm) { return mm / PT_MM_RATIO; };

var _a$a;
var gridDesign0$1 = {
    name: 'editor-default-grid0',
    width: 1300,
    month: { fontSize: 44, fontFamily: 'Open Sans', color: '#000000', align: 'left' },
    year: { fontSize: 44, fontFamily: 'Open Sans', color: '#000000', align: 'right' },
    dayLabel: (_a$a = {
            base: {
                padding: { left: 0, right: 10, top: 5, bottom: 15 },
                label: { fontSize: 18, fontFamily: 'Open Sans', color: '#000000' },
            }
        },
        _a$a[0] = {
            label: { fontSize: 18, fontFamily: 'Open Sans', color: 'red' },
        },
        _a$a),
    weekLabel: {
        number: {
            style: { fontSize: 18, fontFamily: 'Open Sans', color: 'grey' },
            vAlignment: 'top',
            padding: { left: 0, right: 20, top: 12, bottom: 0 },
            size99: 28,
        },
    },
    grid: {
        padding: { left: 0, top: 0, right: 0, bottom: 0 },
        cellSpacing: { horizontal: 12, vertical: 0 },
        cell: {
            base: {
                padding: { left: 0, right: 12, top: 12, bottom: 0 },
                borderTop: { color: 'grey', size: 1 },
                date: { fontSize: 18, fontFamily: 'Open Sans', color: '#000000' },
                eventsPadding: { left: 5, right: 5, top: 5, bottom: 5 },
                eventHAlignment: 'left',
            },
            overrides: [
                {
                    type: 'notInMonth',
                    borderTop: { color: 'rgba(0,0,0,0.3)', size: 1 },
                    date: { fontSize: 18, fontFamily: 'Open Sans', color: 'rgba(0,0,0,0.3)', },
                },
                {
                    type: 0,
                    date: { fontSize: 18, fontFamily: 'Open Sans', color: 'red' },
                }
            ],
        },
    },
};

var gridDesign1$1 = {
    name: 'editor-default-grid1',
    deprecated: true,
    width: 1300,
    month: { fontSize: 44, fontFamily: 'Open Sans', color: 'black', align: 'left' },
    year: { fontSize: 44, fontFamily: 'Open Sans', color: 'black', align: 'right' },
    dayLabel: {
        base: {
            padding: { left: 0, right: 0, top: 5, bottom: 10 },
            label: { fontSize: 18, fontFamily: 'Open Sans', color: 'grey' },
            hAlignment: 'center',
        }
    },
    weekLabel: {
        number: {
            style: { fontSize: 18, fontFamily: 'Open Sans', color: 'grey' },
            vAlignment: 'top',
            padding: { left: 0, right: 10, top: 10, bottom: 0 },
            size99: 28,
        },
    },
    grid: {
        padding: { left: 0, top: 0, right: 0, bottom: 0 },
        cellSpacing: { horizontal: 0, vertical: 0 },
        cell: {
            base: {
                padding: { left: 0, right: 10, top: 10, bottom: 0 },
                borderTop: { color: 'rgba(0, 0, 0, 0.3)', size: 1 },
                borderLeft: { color: 'rgba(0, 0, 0, 0.3)', size: 1 },
                date: { fontSize: 18, fontFamily: 'Open Sans', color: 'rgba(51,51,51)' },
                eventsPadding: { left: 5, right: 5, top: 5, bottom: 5 },
                eventHAlignment: 'left',
            },
            overrides: [
                {
                    type: 'right',
                    borderRight: { color: 'rgba(0, 0, 0, 0.3)', size: 1 },
                },
                {
                    type: 'bottom',
                    borderBottom: { color: 'rgba(0, 0, 0, 0.3)', size: 1 },
                },
                {
                    type: 0,
                    date: { fontSize: 18, fontFamily: 'Open Sans', color: 'red' },
                },
                {
                    type: 'notInMonth',
                    backgroundColor: 'rgba(51,51,51,0.1)',
                    date: { fontSize: 18, fontFamily: 'Open Sans', color: 'rgba(51,51,51,0.3)' },
                },
            ]
        },
    },
};

var gridDesign2$3 = {
    name: 'editor-default-grid2',
    deprecated: true,
    width: 1300,
    month: { fontSize: 44, fontFamily: 'TechnoScriptEF', color: 'black', align: 'left' },
    year: { fontSize: 44, fontFamily: 'TechnoScriptEF', color: 'black', align: 'right' },
    dayLabel: {
        base: {
            padding: { left: 0, right: 0, top: 5, bottom: 10 },
            label: { fontSize: 18, fontFamily: 'TechnoScriptEF', color: 'white' },
            hAlignment: 'center',
        },
    },
    weekLabel: {
        number: {
            style: { fontSize: 18, fontFamily: 'TechnoScriptEF', color: 'white' },
            vAlignment: 'top',
            padding: { left: 10, right: 10, top: 10, bottom: 0 },
            size99: 31,
        },
    },
    grid: {
        padding: { left: 0, top: 0, right: 0, bottom: 0 },
        backgroundColor: 'rgba(51,51,51,0.7)',
        cellSpacing: { horizontal: 0, vertical: 0 },
        cell: {
            base: {
                padding: { left: 0, right: 10, top: 10, bottom: 0 },
                borderTop: { color: 'white', size: 1 },
                borderLeft: { color: 'white', size: 1 },
                date: { fontSize: 18, fontFamily: 'TechnoScriptEF', color: 'white' },
                eventsPadding: { left: 5, right: 5, top: 5, bottom: 5 },
                eventHAlignment: 'left',
            },
            overrides: [
                {
                    type: 'right',
                    borderRight: { color: 'white', size: 1 },
                },
                {
                    type: 'bottom',
                    borderBottom: { color: 'white', size: 1 },
                },
                {
                    type: 0,
                    date: { fontSize: 18, fontFamily: 'TechnoScriptEF', color: 'red' },
                },
                {
                    type: 'notInMonth',
                    backgroundColor: 'rgba(255,255,255,0.5)',
                    date: { fontSize: 18, fontFamily: 'TechnoScriptEF', color: 'white' },
                },
            ]
        },
    },
};

var _a$9;
var gridDesign2$2 = {
    name: 'editor-default-grid3',
    deprecated: true,
    width: 800,
    month: { fontSize: 50, fontFamily: 'Open Sans', color: 'black', align: 'left', type: 'number' },
    year: { fontSize: 35, fontFamily: 'Open Sans', color: 'black', align: 'right' },
    dayLabel: (_a$9 = {
            base: {
                padding: { left: 0, right: 0, top: 0, bottom: 10 },
                label: { fontSize: 25, fontFamily: 'Open Sans', color: 'black' },
                hAlignment: 'center',
            }
        },
        _a$9[0] = {
            label: { fontSize: 25, fontFamily: 'Open Sans', color: '#FFD700' },
        },
        _a$9),
    grid: {
        padding: { left: 0, top: 0, right: 0, bottom: 0 },
        cellSpacing: { horizontal: 10, vertical: 10 },
        cell: {
            base: {
                date: { fontSize: 25, fontFamily: 'Open Sans', color: 'black' },
                vAlignment: 'middle',
                hAlignment: 'center',
                eventsPadding: { left: 5, right: 5, top: 5, bottom: 5 },
            },
            overrides: [
                {
                    type: 'notInMonth',
                    date: { fontSize: 25, fontFamily: 'Open Sans', color: 'transparent' },
                },
                {
                    type: 0,
                    date: { fontSize: 25, fontFamily: 'Open Sans', color: '#FFD700' },
                }
            ]
        },
    },
};

var _a$8;
var gridDesign0 = {
    name: 'editor-default-grid4',
    width: 1300,
    month: { fontSize: 56, fontFamily: 'Poiret One', color: '#000000', align: 'left', fontTransform: 'uppercase' },
    year: { fontSize: 56, fontFamily: 'Poiret One', color: '#000000', align: 'right' },
    dayLabel: (_a$8 = {
            base: {
                padding: { left: 0, right: 10, top: 5, bottom: 15 },
                label: { fontSize: 18, fontFamily: 'Poiret One', color: '#000000', fontTransform: 'uppercase' },
                hAlignment: 'center',
            }
        },
        _a$8[0] = {
            label: { fontSize: 18, fontFamily: 'Poiret One', color: 'red', fontTransform: 'uppercase' },
        },
        _a$8),
    weekLabel: {
        number: {
            style: { fontSize: 14, fontFamily: 'Poiret One', color: 'grey' },
            vAlignment: 'top',
            padding: { left: 0, right: 20, top: 10, bottom: 0 },
            size99: 28,
        },
    },
    grid: {
        padding: { left: 0, top: 0, right: 0, bottom: 0 },
        cellSpacing: { horizontal: 12, vertical: 0 },
        cell: {
            base: {
                padding: { left: 6, right: 0, top: 18, bottom: 0 },
                borderTop: { color: '#000000', size: 1 },
                date: { fontSize: 18, fontFamily: 'Poiret One', color: '#000000' },
                hAlignment: 'left',
                eventsPadding: { left: 5, right: 5, top: 5, bottom: 5 },
            },
            overrides: [
                {
                    type: 0,
                    date: { fontSize: 18, fontFamily: 'Poiret One', color: 'red' },
                },
                {
                    type: 'notInMonth',
                    borderTop: { color: 'rgba(229,229,229,1)', size: 1 },
                    date: { fontSize: 18, fontFamily: 'Poiret One', color: 'rgba(229,229,229,1)', },
                },
                {
                    type: 'top',
                    borderTop: { color: '#000000', size: 3 },
                },
            ]
        },
    },
};

var gridDesign1 = {
    name: 'editor-default-grid5',
    width: 1300,
    month: { fontSize: 56, fontFamily: 'Notable', color: '#000000', align: 'left' },
    year: { fontSize: 56, fontFamily: 'Notable', color: '#000000', align: 'right' },
    dayLabel: {
        base: {
            padding: { left: 0, right: 0, top: 5, bottom: 10 },
            label: { fontSize: 14, fontFamily: 'Notable', color: '#000000', fontTransform: 'uppercase' },
            hAlignment: 'center',
            display: 'long'
        }
    },
    weekLabel: {
        number: {
            style: { fontSize: 14, fontFamily: 'Notable', color: '#C6C6C6' },
            vAlignment: 'top',
            padding: { left: 0, right: 10, top: 20, bottom: 0 },
            size99: 28,
        },
    },
    grid: {
        padding: { left: 0, top: 0, right: 0, bottom: 0 },
        cellSpacing: { horizontal: 12, vertical: 12 },
        cell: {
            base: {
                padding: { left: 0, right: 20, top: 20, bottom: 0 },
                backgroundColor: 'rgba(247, 247, 247, 1)',
                date: { fontSize: 14, fontFamily: 'Notable', color: 'rgba(94, 94, 94, 1)' },
                eventsPadding: { left: 5, right: 5, top: 5, bottom: 5 },
            },
            overrides: [
                {
                    type: 0,
                    backgroundColor: 'rgba(229, 229, 229, 1)',
                },
                {
                    type: 'notInMonth',
                    date: { fontSize: 14, fontFamily: 'Notable', color: 'rgba(0, 0, 0, 0.2)' },
                },
            ]
        },
    },
};

var _a$7;
var gridDesign2$1 = {
    name: 'editor-default-grid6',
    width: 1300,
    month: { fontSize: 56, fontFamily: 'TechnoScriptEF', color: '#000000', align: 'left' },
    year: { fontSize: 56, fontFamily: 'TechnoScriptEF', color: '#000000', align: 'right' },
    dayLabel: (_a$7 = {
            base: {
                padding: { left: 0, right: 0, top: 20, bottom: 20 },
                label: { fontSize: 15, fontFamily: 'TechnoScriptEF', color: '#000000' },
                hAlignment: 'center',
                display: 'long'
            }
        },
        _a$7[0] = {
            label: { fontSize: 15, fontFamily: 'TechnoScriptEF', color: '#F93F1E' },
        },
        _a$7),
    weekLabel: {
        number: {
            style: { fontSize: 14, fontFamily: 'TechnoScriptEF', color: '#C6C6C6' },
            vAlignment: 'top',
            padding: { left: 10, right: 10, top: 10, bottom: 0 },
            size99: 31,
        },
    },
    grid: {
        padding: { left: 0, top: 0, right: 0, bottom: 0 },
        cellSpacing: { horizontal: 0, vertical: 0 },
        cell: {
            base: {
                padding: { left: 0, right: 10, top: 10, bottom: 0 },
                date: { fontSize: 14, fontFamily: 'TechnoScriptEF', color: '#000000' },
                borderTop: { color: '#000000', size: 1 },
                borderBottom: { color: '#000000', size: 1 },
                borderLeft: { color: '#000000', size: 1 },
                borderRight: { color: '#000000', size: 1 },
                eventsPadding: { left: 5, right: 5, top: 5, bottom: 5 },
            },
            overrides: [
                {
                    type: 0,
                    date: { fontSize: 14, fontFamily: 'TechnoScriptEF', color: '#F93F1E' },
                },
                {
                    type: 'notInMonth',
                    backgroundColor: '#F7F7F7',
                    date: { fontSize: 14, fontFamily: 'TechnoScriptEF', color: 'rgba(0, 0, 0, 0.40)' },
                }
            ]
        },
    },
};

var _a$6;
var gridDesign2 = {
    name: 'editor-default-grid7',
    width: 800,
    month: { fontSize: 56, fontFamily: 'Oswald', color: '#000000', align: 'left', fontTransform: 'uppercase' },
    year: { fontSize: 56, fontFamily: 'Oswald', color: '#000000', align: 'right' },
    dayLabel: (_a$6 = {
            base: {
                padding: { left: 0, right: 0, top: 10, bottom: 10 },
                label: { fontSize: 14, fontFamily: 'Oswald', color: '#898989', fontTransform: 'uppercase' },
                hAlignment: 'center',
            }
        },
        _a$6[0] = {
            label: { fontSize: 14, fontFamily: 'Oswald', color: '#000000', fontTransform: 'uppercase' },
        },
        _a$6),
    weekLabel: {
        number: {
            style: { fontSize: 14, fontFamily: 'Oswald', color: '#C6C6C6' },
            vAlignment: 'middle',
            padding: { left: 10, right: 15, top: 0, bottom: 0 },
            size99: 31,
        },
    },
    grid: {
        padding: { left: 0, top: 0, right: 0, bottom: 0 },
        cellSpacing: { horizontal: 10, vertical: 10 },
        cell: {
            base: {
                date: { fontSize: 25, fontFamily: 'Oswald', color: '#898989' },
                vAlignment: 'middle',
                hAlignment: 'center',
                eventsPadding: { left: 0, right: 0, top: 5, bottom: 0 },
            },
            overrides: [
                {
                    type: 0,
                    date: { fontSize: 25, fontFamily: 'Oswald', color: '#000000' },
                },
                {
                    type: 'notInMonth',
                    date: { fontSize: 25, fontFamily: 'Oswald', color: 'transparent' },
                }
            ]
        },
    },
};

var _a$5;
var gridDesign8$1 = {
    name: 'editor-default-grid8',
    width: 1200,
    month: { fontSize: 56, fontFamily: 'Futura', color: '#000000', align: 'left', fontTransform: 'uppercase' },
    year: { fontSize: 56, fontFamily: 'Futura', color: '#000000', align: 'right' },
    dayLabel: (_a$5 = {
            base: {
                padding: { left: 0, right: 0, top: 10, bottom: 10 },
                label: { fontSize: 14, fontFamily: 'Futura', color: 'rgba(0, 0, 0, 0.5)', fontTransform: 'uppercase' },
                hAlignment: 'center',
            }
        },
        _a$5[0] = {
            label: { fontSize: 14, fontFamily: 'Futura', color: '#000000', fontTransform: 'uppercase' },
        },
        _a$5),
    weekLabel: {
        number: {
            style: { fontSize: 14, fontFamily: 'Futura', color: 'rgba(0, 0, 0, 0.3)' },
            vAlignment: 'top',
            hAlignment: 'center',
            padding: { left: 0, right: 10, top: 10, bottom: 0 },
            size99: 31,
        },
    },
    grid: {
        padding: { left: 0, top: 0, right: 0, bottom: 0 },
        cellSpacing: { horizontal: 0, vertical: 0 },
        cell: {
            base: {
                date: { fontSize: 36, fontFamily: 'Futura', color: 'rgba(0, 0, 0, 0.5)' },
                vAlignment: 'top',
                hAlignment: 'right',
                borderRight: { size: 1, color: '#000000' },
                padding: { left: 10, right: 10, top: 10, bottom: 10 },
                eventsPadding: { left: 5, right: 5, top: 0, bottom: 10 },
                eventHAlignment: 'left'
            },
            overrides: [
                {
                    type: 0,
                    date: { fontSize: 36, fontFamily: 'Futura', color: '#000000' },
                },
                {
                    type: 'notInMonth',
                    date: { fontSize: 36, fontFamily: 'Futura', color: 'rgba(0, 0, 0, 0.1)' },
                }
            ]
        },
    },
};

var _a$4;
var gridDesign8 = {
    name: 'editor-default-grid8-dark',
    presentationBackgroundColor: '#383838',
    width: 1200,
    month: { fontSize: 56, fontFamily: 'Futura', color: '#ffffff', align: 'left', fontTransform: 'uppercase' },
    year: { fontSize: 56, fontFamily: 'Futura', color: '#ffffff', align: 'right' },
    dayLabel: (_a$4 = {
            base: {
                padding: { left: 0, right: 0, top: 10, bottom: 10 },
                label: { fontSize: 14, fontFamily: 'Futura', color: 'rgba(255, 255, 255, 0.7)', fontTransform: 'uppercase' },
                hAlignment: 'center',
            }
        },
        _a$4[0] = {
            label: { fontSize: 14, fontFamily: 'Futura', color: '#ffffff', fontTransform: 'uppercase' },
        },
        _a$4),
    weekLabel: {
        number: {
            style: { fontSize: 14, fontFamily: 'Futura', color: 'rgba(255, 255, 255, 0.5)' },
            vAlignment: 'top',
            hAlignment: 'center',
            padding: { left: 0, right: 10, top: 10, bottom: 0 },
            size99: 31,
        },
    },
    grid: {
        padding: { left: 0, top: 0, right: 0, bottom: 0 },
        cellSpacing: { horizontal: 0, vertical: 0 },
        cell: {
            base: {
                date: { fontSize: 36, fontFamily: 'Futura', color: 'rgba(255, 255, 255, 0.7)' },
                vAlignment: 'top',
                hAlignment: 'right',
                borderRight: { size: 1, color: '#ffffff' },
                padding: { left: 10, right: 10, top: 10, bottom: 10 },
                eventsPadding: { left: 5, right: 5, top: 0, bottom: 10 },
                eventHAlignment: 'left'
            },
            overrides: [
                {
                    type: 0,
                    date: { fontSize: 36, fontFamily: 'Futura', color: '#ffffff' },
                },
                {
                    type: 'notInMonth',
                    date: { fontSize: 36, fontFamily: 'Futura', color: 'rgba(255, 255, 255, 0.2)' },
                }
            ]
        },
    },
};

var _a$3;
var design$3 = {
    name: 'editor-default-grid9',
    width: 1200,
    month: { fontSize: 56, fontFamily: 'Josefin Sans', color: '#000000', align: 'left', fontTransform: 'uppercase' },
    year: { fontSize: 56, fontFamily: 'Josefin Sans', color: '#000000', align: 'right' },
    dayLabel: (_a$3 = {
            base: {
                padding: { left: 0, right: 0, top: 0, bottom: 10 },
                label: { fontSize: 18, fontFamily: 'Josefin Sans', color: 'rgba(0, 0, 0, 0.5)', fontTransform: 'uppercase' },
                hAlignment: 'left',
            }
        },
        _a$3[0] = {
            label: { fontSize: 18, fontFamily: 'Josefin Sans', color: '#000000', fontTransform: 'uppercase' },
        },
        _a$3),
    weekLabel: {
        number: {
            style: { fontSize: 19, fontFamily: 'Josefin Sans', color: 'rgba(0, 0, 0, 0.3)' },
            vAlignment: 'top',
            hAlignment: 'center',
            padding: { left: 0, right: 15, top: 0, bottom: 0 },
            size99: 31,
        },
    },
    grid: {
        padding: { left: 0, top: 0, right: 0, bottom: 0 },
        cellSpacing: { horizontal: 15, vertical: 15 },
        cell: {
            base: {
                date: { fontSize: 16, fontFamily: 'Josefin Sans', color: '#000000' },
                vAlignment: 'top',
                hAlignment: 'left',
                padding: { left: 10, right: 10, top: 10, bottom: 10 },
                eventsPadding: { left: 5, right: 5, top: 0, bottom: 5 },
                eventHAlignment: 'left',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                borderRadius: 16,
            },
            overrides: [
                {
                    type: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                },
                {
                    type: 'notInMonth',
                    backgroundColor: 'rgba(0, 0, 0, 0.02',
                    date: { fontSize: 16, fontFamily: 'Josefin Sans', color: 'rgba(0, 0, 0, 0.3)' },
                }
            ]
        },
    },
};

var _a$2;
var design$2 = {
    name: 'editor-default-grid9-dark',
    presentationBackgroundColor: '#383838',
    width: 1200,
    month: { fontSize: 56, fontFamily: 'Josefin Sans', color: '#ffffff', align: 'left', fontTransform: 'uppercase' },
    year: { fontSize: 56, fontFamily: 'Josefin Sans', color: '#ffffff', align: 'right' },
    dayLabel: (_a$2 = {
            base: {
                padding: { left: 0, right: 0, top: 0, bottom: 10 },
                label: { fontSize: 18, fontFamily: 'Josefin Sans', color: 'rgba(255, 255, 255, 0.7)', fontTransform: 'uppercase' },
                hAlignment: 'left',
            }
        },
        _a$2[0] = {
            label: { fontSize: 18, fontFamily: 'Josefin Sans', color: '#ffffff', fontTransform: 'uppercase' },
        },
        _a$2),
    weekLabel: {
        number: {
            style: { fontSize: 19, fontFamily: 'Josefin Sans', color: 'rgba(255, 255, 255, 0.7)' },
            vAlignment: 'top',
            hAlignment: 'center',
            padding: { left: 0, right: 15, top: 0, bottom: 0 },
            size99: 31,
        },
    },
    grid: {
        padding: { left: 0, top: 0, right: 0, bottom: 0 },
        cellSpacing: { horizontal: 15, vertical: 15 },
        cell: {
            base: {
                date: { fontSize: 16, fontFamily: 'Josefin Sans', color: '#ffffff' },
                vAlignment: 'top',
                hAlignment: 'left',
                padding: { left: 10, right: 10, top: 10, bottom: 10 },
                eventsPadding: { left: 5, right: 5, top: 0, bottom: 5 },
                eventHAlignment: 'left',
                backgroundColor: 'rgba(255, 255, 255, 0.30)',
                borderRadius: 16,
            },
            overrides: [
                {
                    type: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                {
                    type: 'notInMonth',
                    backgroundColor: 'rgba(255, 255, 255, 0.2',
                    date: { fontSize: 16, fontFamily: 'Josefin Sans', color: 'rgba(255, 255, 255, 0.3)' },
                }
            ]
        },
    },
};

var _a$1;
var design$1 = {
    name: 'editor-default-grid10',
    width: 1200,
    month: { fontSize: 56, fontFamily: 'Abril Fatface', color: '#000000', align: 'left' },
    year: { fontSize: 56, fontFamily: 'Abril Fatface', color: '#000000', align: 'right' },
    dayLabel: (_a$1 = {
            base: {
                padding: { left: 0, right: 0, top: 10, bottom: 10 },
                label: { fontSize: 16, fontFamily: 'Josefin Sans', color: 'rgba(0, 0, 0, 0.5)', fontTransform: 'uppercase' },
                hAlignment: 'center',
            }
        },
        _a$1[0] = {
            label: { fontSize: 16, fontFamily: 'Josefin Sans', color: '#000000', fontTransform: 'uppercase' },
        },
        _a$1),
    weekLabel: {
        number: {
            style: { fontSize: 16, fontFamily: 'Josefin Sans', color: 'rgba(0, 0, 0, 0.3)' },
            vAlignment: 'top',
            hAlignment: 'center',
            padding: { left: 10, right: 10, top: 0, bottom: 0 },
            size99: 31,
        },
    },
    grid: {
        padding: { left: 0, top: 0, right: 0, bottom: 0 },
        cellSpacing: { horizontal: 0, vertical: 15 },
        cell: {
            base: {
                date: { fontSize: 20, fontFamily: 'Abril Fatface', color: 'rgba(0, 0, 0, 0.5)' },
                vAlignment: 'top',
                hAlignment: 'center',
                padding: { left: 10, right: 10, top: 10, bottom: 10 },
                eventsPadding: { left: 5, right: 5, top: 0, bottom: 5 },
                eventHAlignment: 'left',
                borderRight: { size: 1, color: '#000000' },
                eventsText: { fontSize: 16, fontFamily: 'Barlow Condensed', color: '#000000' },
            },
            overrides: [
                {
                    type: 0,
                    date: { fontSize: 20, fontFamily: 'Abril Fatface', color: '#000000' },
                },
                {
                    type: 'notInMonth',
                    date: { fontSize: 20, fontFamily: 'Abril Fatface', color: 'rgba(0, 0, 0, 0.1)' },
                }
            ]
        },
    },
};

var _a;
var design = {
    name: 'editor-default-grid10-dark',
    presentationBackgroundColor: '#383838',
    width: 1200,
    month: { fontSize: 56, fontFamily: 'Abril Fatface', color: '#ffffff', align: 'left' },
    year: { fontSize: 56, fontFamily: 'Abril Fatface', color: '#ffffff', align: 'right' },
    dayLabel: (_a = {
            base: {
                padding: { left: 0, right: 0, top: 10, bottom: 10 },
                label: { fontSize: 16, fontFamily: 'Josefin Sans', color: 'rgba(255, 255, 255, 0.5)', fontTransform: 'uppercase' },
                hAlignment: 'center',
            }
        },
        _a[0] = {
            label: { fontSize: 16, fontFamily: 'Josefin Sans', color: '#ffffff', fontTransform: 'uppercase' },
        },
        _a),
    weekLabel: {
        number: {
            style: { fontSize: 16, fontFamily: 'Josefin Sans', color: 'rgba(255, 255, 255, 0.3)' },
            vAlignment: 'top',
            hAlignment: 'center',
            padding: { left: 10, right: 10, top: 0, bottom: 0 },
            size99: 31,
        },
    },
    grid: {
        padding: { left: 0, top: 0, right: 0, bottom: 0 },
        cellSpacing: { horizontal: 0, vertical: 15 },
        cell: {
            base: {
                date: { fontSize: 20, fontFamily: 'Abril Fatface', color: 'rgba(255, 255, 255, 0.5)' },
                vAlignment: 'top',
                hAlignment: 'center',
                padding: { left: 10, right: 10, top: 10, bottom: 10 },
                eventsPadding: { left: 5, right: 5, top: 0, bottom: 5 },
                eventHAlignment: 'left',
                borderRight: { size: 1, color: '#ffffff' },
                eventsText: { fontSize: 16, fontFamily: 'Barlow Condensed', color: '#ffffff' },
            },
            overrides: [
                {
                    type: 0,
                    date: { fontSize: 20, fontFamily: 'Abril Fatface', color: '#ffffff' },
                },
                {
                    type: 'notInMonth',
                    date: { fontSize: 20, fontFamily: 'Abril Fatface', color: 'rgba(255, 255, 255, 0.1)' },
                }
            ]
        },
    },
};

var gridDesigns = [
    gridDesign0$1, gridDesign0, gridDesign1, gridDesign2$1, gridDesign2, gridDesign8$1, gridDesign8, design$3, design$2, design$1, design,
    // deprecated
    gridDesign1$1, gridDesign2$3, gridDesign2$2
];
var defaultGridDesign = gridDesigns[0];

var generatorUtils = {
    getCellContext: getCellContext,
    getGridContext: getGridContext,
    getWeekNumberColumnWidth: getWeekNumberColumnWidth,
    getWeekdayBarHeight: getWeekdayBarHeight,
    getWeekNumber: getWeekNumber,
    getToSizeFn: getToSizeFn,
};
var designUtils = {
    getDesignValue: getCellDesignValue,
    getApplicationDesigns: getApplicableCellDesign,
    textAlignToTextAnchor: hAlignToTextAnchor,
    xTextAlign: xTextAlign,
    yTextAlign: yTextAlign,
    mm2pt: mm2pt,
};
var spreadUtils = {
    getSpreadDate: getSpreadDate,
    isSpreadNameAMonth: isSpreadNameAMonth,
};
var designs = {
    gridDesigns: gridDesigns,
    defaultGridDesign: defaultGridDesign
};

exports.designUtils = designUtils;
exports.designs = designs;
exports.generateGridAsSVG = generateGridAsSVG;
exports.generatorUtils = generatorUtils;
exports.spreadUtils = spreadUtils;
